<template>
  <section class="flex flex-col gap-2">
    <transition-group
      name="list"
      tag="div"
      class="flex flex-col gap-2"
    >
      <div
        v-for="(field, index) in fields"
        :key="index"
        class="relative w-full"
        :class="{ 'mt-8': index > 0 }"
      >
        <input
          v-model="field.full_name"
          type="text"
          class="h-12 w-full rounded border border-transparent bg-off-white p-2 text-sm placeholder-gray-over-dark hover:border-placeholder-gray focus:bg-white focus:text-dark-gray"
          placeholder="Enter full name of person or department"
        />
        <div class="mt-2 flex gap-2">
          <input
            v-model="field.email"
            type="email"
            class="h-12 w-full rounded border border-transparent bg-off-white p-2 text-sm placeholder-gray-over-dark hover:border-placeholder-gray focus:bg-white focus:text-dark-gray"
            placeholder="Enter email"
          />
          <input
            v-model="field.phone"
            v-mask="'###-###-####'"
            type="tel"
            class="h-12 w-full rounded border border-transparent bg-off-white p-2 text-sm placeholder-gray-over-dark hover:border-placeholder-gray focus:bg-white focus:text-dark-gray"
            placeholder="Enter phone #"
          />
        </div>
        <div
          v-if="index > 0"
          class="absolute right-0 flex w-8 cursor-pointer items-center justify-center text-placeholder-gray hover:text-gray-over-dark"
          style="top: 16%"
          @click="removeField(index)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="19"
            viewBox="0 0 16 19"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.99134 2.65885C6.4132 2.237 6.98536 2 7.58196 2C8.17855 2 8.75071 2.237 9.17257 2.65885C9.34678 2.83306 9.48946 3.0329 9.59695 3.24951H5.56696C5.67445 3.0329 5.81714 2.83306 5.99134 2.65885ZM3.4518 3.24951C3.63409 2.49669 4.02039 1.80138 4.57713 1.24464C5.37406 0.447711 6.45493 0 7.58196 0C8.70898 0 9.78985 0.447709 10.5868 1.24464C11.1435 1.80138 11.5298 2.49669 11.7121 3.24951H11.9363L11.9552 3.2496H15.1642V5.2496H13.9363C13.9363 5.28895 13.9351 5.32854 13.9327 5.36835L13.2781 16.3662C13.2152 17.4228 12.3401 18.2474 11.2816 18.2474H3.88195C2.82353 18.2474 1.94837 17.4228 1.88548 16.3662L1.23084 5.36835C1.22847 5.32854 1.22728 5.28895 1.22724 5.2496H0V3.2496H3.20835L3.22731 3.24951H3.4518ZM3.88194 16.2474L3.22731 5.2496H11.9363L11.2816 16.2474H3.88194ZM4.68652 14.336L4.68652 6.75391H6.68652L6.68652 14.336H4.68652ZM8.47754 6.75391V14.336H10.4775V6.75391H8.47754Z"
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
    </transition-group>
    <button
      class="mt-2 flex w-full items-center justify-center gap-2 rounded-md border p-2"
      :class="[
        canAddAnother
          ? 'border-gray-over-dark text-gray-over-dark'
          : 'cursor-not-allowed border-light-gray text-light-gray',
      ]"
      @click="addField"
    >
      <span class="text-sm">Add Another</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="14"
        viewBox="0 0 13 14"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.49871 6L0.433593 6L0.433595 8L5.49871 8L5.49871 13.0645L7.49871 13.0645L7.4987 8L12.5632 8V6L7.4987 6L7.4987 0.934889L5.4987 0.934889L5.49871 6Z"
          fill="currentColor"
        />
      </svg>
    </button>
  </section>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { isEmail, isPhone } from '@/common/validators.js';

const props = defineProps({
  dataStored: {
    type: Array,
    default: () => [],
    required: false,
  },
});

const emit = defineEmits(['update:fields']);

const fields = ref(JSON.parse(JSON.stringify(props.dataStored)));

const addField = () => {
  if (!canAddAnother.value) return;
  fields.value.push({ full_name: '', email: '', phone: '' });
};

const removeField = (index) => {
  if (fields.value[index]?.id) emit('delete', fields.value[index].id);
  fields.value.splice(index, 1);
};

const canAddAnother = computed(() => {
  const firstField = fields.value[0];
  if (
    !firstField.full_name ||
    !isEmail(firstField.email) ||
    !isPhone(firstField.phone)
  ) {
    return false;
  }
  return true;
});

watch(
  fields,
  (newFields) => {
    emit('update:fields', newFields);
  },
  { deep: true }
);

watch(
  () => props.dataStored,
  (data) => {
    if (!fields.value) return;

    if (fields.value[0].full_name.length == 0) {
      fields.value = data;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}
</style>
